<template>
  <v-app v-if="loaded" id="app">
    <div v-if="loading" class="d-flex align-center justify-center pa-8">
      <v-progress-circular color="primary" indeterminate />
    </div>

    <div v-else>
      <NavDrawer
        v-if="_authenticated"
        ref="navDrawer"
        @change-status="changeStatus($event)"
        @sign-out="signOut()"
        @notification-bell="playNotificationSound()"
        @pause-cleared="handlePauseCleared"
      />
      <FilterDrawer
        v-if="_authenticated"
        ref="filterDrawer"
        v-model="filterDrawer"
        :hideFilters="_hidedFilters"
      />
      <v-main class="background" style="position: relative">
        <div class="d-flex flex-row justify-space-between">
          <v-app-bar-nav-icon
            v-if="_showNavIcon"
            class="ml-2 mt-2"
            @click.stop="handleDrawer()"
          />
          <v-tooltip bottom v-if="_isFilterableRouter">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
                <v-badge
                  bottom
                  left
                  overlap
                  offset-x="16"
                  offset-y="16"
                  :content="filtersWithSelectedItems.length"
                  :color="
                    filtersWithSelectedItems.length ? 'primary' : 'transparent'
                  "
                >
                  <v-btn
                    class="mx-2 mt-2 grey"
                    icon
                    dark
                    @click="filterDrawer = !filterDrawer"
                  >
                    <v-icon>mdi-filter</v-icon>
                  </v-btn>
                </v-badge>
              </div>
            </template>
            <span>{{ _filtersInfoText }}</span>
          </v-tooltip>
        </div>

        <router-view />
      </v-main>

      <v-dialog v-model="newAttendanceDialog" persistent :max-width="500">
        <v-card class="rounded-lg pa-2" elevation="3" width="500">
          <v-card-title class="my-auto">
            Nova solicitação de atendimento

            <v-spacer />

            <v-btn
              v-if="_isAdmin || _isModerator"
              icon
              @click="newAttendanceDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider class="mt-2" />

          <v-card-text class="text-center mt-4">
            <p>
              <strong>{{ $t("group") }}:</strong>
              {{ newAttendanceInfo?.groupName || "—" }}
            </p>
            <p>
              <strong>{{ $t("subgroup") }}:</strong>
              {{ newAttendanceInfo?.subgroupName || "—" }}
            </p>
            <p>
              <strong>{{ $t("desk") }}:</strong>
              {{ newAttendanceInfo?.deskName || "—" }}
            </p>

            <p class="mt-4 mx-auto">Clique para iniciar o atendimento.</p>
          </v-card-text>

          <v-card-actions class="justify-center">
            <v-btn
              color="success"
              @click="handleStartAttendance"
              class="button-pulse"
            >
              <v-icon>mdi-phone-in-talk</v-icon>
              Atender
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="newAppointmentDialog" persistent :max-width="500">
        <v-card class="rounded-lg pa-2" elevation="3" width="500">
          <v-card-title class="my-auto">
            Nova solicitação de atendimento

            <v-spacer />

            <v-btn icon @click="newAppointmentDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider class="mt-2" />

          <v-card-text class="text-center mt-4">
            <p>
              <strong>{{ $t("group") }}:</strong>
              {{ newAppointmentInfo?.groupName || "—" }}
            </p>
            <p>
              <strong>{{ $t("subgroup") }}:</strong>
              {{ newAppointmentInfo?.subgroupName || "—" }}
            </p>
            <p>
              <strong>Gabinete:</strong>
              {{ newAppointmentInfo?.deskName || "—" }}
            </p>

            <p class="mt-4 mx-auto">Clique para iniciar o atendimento.</p>
          </v-card-text>

          <v-card-actions class="justify-center">
            <v-btn
              color="success"
              class="button-pulse"
              @click="handleStartMeeting(newAppointment)"
            >
              <v-icon>mdi-phone-in-talk</v-icon>
              Atender
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <LoaderHover v-model="loadingOverlay" />
      <!-- floating button -->
      <v-btn
        v-if="_attendanceInProgress"
        class="button-pulse"
        fab
        dark
        fixed
        bottom
        right
        color="primary"
        @click="toAttendance()"
      >
        <v-icon>mdi-phone-in-talk</v-icon>
      </v-btn>

      <AlertBar ref="alertBar" />

      <VersionBadge />

      <FloatingUpload v-if="_authenticated" />

      <FloatingDownload v-if="_authenticated" />
    </div>
  </v-app>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { getCounter, getCounters } from "./services/counters";
import { getCourt, getCourts } from "./services/courts";
import { getJurisdiction, getJurisdictions } from "./services/jurisdictions";
import { updateUser, isLinkedPeople } from "@/services/users";
import { loadSession, signOut } from "@/services/auth";
import { startBreak } from "@/services/pauses";
import {
  getAttendances,
  getAttendancesWaitingMe,
  getAttendancesInProgressMe,
  getAttendance,
} from "./services/attendances";
import {
  getAppointmentById,
  startAppointment,
  getAppointmentsWaitingMe,
} from "@/services/appointments";
import { Howl, Howler } from "howler";
import { eventBus } from "@/eventBus.js";
import { connectSocket, disconnectSocket } from "./socket";
import {
  attendanceJoin,
  pingEmit,
  startAttendance,
} from "@/socket/attendances";
import usePauseTimer from "@/composables/usePauseTimer";
import VersionBadge from "@/components/versionBadge/VersionBadge.vue";
import NavDrawer from "./components/navDrawer/NavDrawer";
import AlertBar from "./components/alertBar/AlertBar.vue";
import FilterDrawer from "./components/filterDrawer/FilterDrawer.vue";
import FloatingUpload from "./components/floatingUpload/FloatingUpload";
import FloatingDownload from "./components/floatingDownload/FloatingDownload";

export default {
  name: "App",

  components: {
    NavDrawer,
    AlertBar,
    FilterDrawer,
    VersionBadge,
    FloatingUpload,
    FloatingDownload,
  },

  data() {
    return {
      loading: false,
      newAttendanceDialog: false,
      newAttendanceInfo: {
        groupName: "",
        subgroupName: "",
        deskName: "",
      },
      newAttendanceID: null,
      newAppointmentDialog: false,
      newAppointmentInfo: {
        groupName: "",
        subgroupName: "",
        deskName: "",
      },
      newAppointment: null,
      loaded: false,
      loadingOverlay: false,
      notificationSoundInterval: null,
      filterDrawer: false,
      filtersRoutes: [
        "Reports",
        "QueueAttendance",
        "TimeAttendance",
        "Satisfaction",
        "ScheduleQueueAttendance",
        "ScheduleTimeAttendance",
        "ScheduleSatisfaction",
        "Quit",
        "QueueWaitingTime",
        "ConsolidatedQueueAttendance",
        // "Queue",
        // "Waiting",
        // "InProgress",
      ],
      interval: null,
      filtersWithSelectedItems: [],
    };
  },

  watch: {
    newAttendanceDialog(value) {
      if (value) {
        this.startNotificationSound();
      } else {
        this.stopNotificationSound();
      }
    },
    newAppointmentDialog(value) {
      if (value) {
        this.startNotificationSound();
      } else {
        this.stopNotificationSound();
      }
    },
    $route() {
      if (!this._isAdmin && !this._isModerator && !this._isAudienceAdmin)
        this.handleIsLinked();
    },
    attendance(value, oldValue) {
      if (!value) {
        return;
      }

      const fromAttendanceID =
        oldValue?._id || oldValue?.id || oldValue?.attendanceID;
      const toAttendanceID = value?._id || value?.id || value?.attendanceID;

      const isDiff = fromAttendanceID !== toAttendanceID;
      if (!isDiff) {
        return;
      }

      this.toAttendance();
    },
    authenticated(value, oldValue) {
      if (!value) {
        console.log("watch:authenticated -> cleanupSocketListeners");
        this.cleanupSocketListeners();
        clearInterval(this.interval);
        return;
      }

      // authenticating
      if (!oldValue && value) {
        console.log("watch:authenticated -> setupSocketListeners");
        this.setupSocketListeners();
        this.handlePingSession();
        return;
      }
    },
    refreshToken(newVal, oldVal) {
      this.handleChangeToken(newVal, oldVal);
    },
    filters: {
      handler(val) {
        if (!val) this.filtersWithSelectedItems = [];

        this.filtersWithSelectedItems = Object.keys(
          this.filters ? this.filters : {}
        ).filter((key) => {
          const options = ["groups", "subgroups", "desks", "attendants"];

          if (options.includes(key)) {
            const filters = this.filters[key];

            if (filters && Array.isArray(filters)) return !!filters?.length > 0;

            if (filters && typeof filters === "object")
              return Object.keys(filters)?.length;

            return !!filters;
          }

          return false;
        });
      },
      deep: true,
      immediate: true,
    },
  },

  computed: {
    ...mapState(["user", "attendance", "filters"]),
    ...mapGetters([
      "authenticated",
      "refreshToken",
      "attendanceRole",
      "audienceRole",
      "linkedAccess",
      "attendanceAccess",
      "audienceAccess",
    ]),

    _isOperator() {
      return this.attendanceRole === "operator";
    },

    _isModerator() {
      return this.attendanceRole === "moderator";
    },

    _isAdmin() {
      return this.attendanceRole === "admin";
    },

    _isSupervisor() {
      return this.attendanceRole === "supervisor";
    },

    _isAudienceAdmin() {
      return this.audienceRole === "admin";
    },

    _isAvailable() {
      if (!this._authenticated) {
        return false;
      }

      const user = this.user;

      if (!user || !!this.attendance) {
        return false;
      }

      if (!this.linkedAccess) return false;

      if (user.status !== "online") {
        console.log("watch:isAvailable:offline");
        return false;
      }

      if (!this.linkedAccess) return false;

      const isAdmin = this._isModerator || this._isAdmin || this._isSupervisor;

      if (isAdmin) {
        const preference = user.settings?.attendancesNotification;
        console.log("watch:isAvailable:admin", !!preference);
        return !!preference;
      }

      console.log("watch:isAvailable:attendant", true);
      return true;
    },

    _authenticated() {
      return this.$store.getters.authenticated;
    },
    _attendanceInProgress() {
      return this.authenticated && this.attendance?.status === "in-progress";
    },
    _showNavIcon() {
      return (
        this._authenticated &&
        this.$route.name !== "Chat" &&
        this.$route.name !== "Details" &&
        this.$route.name !== "OmniChannel"
      );
    },
    _isFilterableRouter() {
      return this.filtersRoutes.includes(this.$route.name);
    },
    _filtersInfoText() {
      const { length } = this.filtersWithSelectedItems || [];
      if (!length) {
        return "Nenhum filtro aplicado";
      }

      if (length === 1) {
        return `1 filtro aplicado`;
      }

      return `${length} filtros aplicados`;
    },

    _hidedFilters() {
      const routeName = this.$route.name;
      const hidedRoutes = ["Quit", "ConsolidatedQueueAttendance"];

      const filters = [
        ...(hidedRoutes.includes(routeName) ? ["attendant"] : []),
      ];

      return filters;
    },
  },

  created() {
    this.handleSession();
    // Get functions from the usePauseTimer composable
    const { startPause, getPauseStart, getPauseEnd } = usePauseTimer();
    this.startPause = startPause;

    // Use getter functions to access pause times
    this.getPauseStart = getPauseStart;
    this.getPauseEnd = getPauseEnd;

    // Verificar o estado inicial e conectar/desconectar o socket conforme necessário
    if (this.authenticated) {
      console.log("Inicialmente autenticado. Conectando ao socket...");
      connectSocket();
      this.handlePingSession();
    } else {
      console.log(
        "Inicialmente não autenticado. Garantindo que o socket esteja desconectado..."
      );
      disconnectSocket();
      clearInterval(this.interval);
    }
  },

  mounted() {
    this.setupEventListeners();

    if (this.authenticated) {
      if (this.attendance) {
        if (this._attendanceInProgress) {
          this.handleJoinAttendance();
        }

        this.toAttendance();
      }

      return;
    }

    this.loadingOverlay = false;
  },

  beforeDestroy() {
    this.cleanupEventListeners();
    clearInterval(this.interval);
  },

  methods: {
    ...mapActions([
      "setUser",
      "setSignOut",
      "setAttendance",
      "setUserStatus",
      "clearFilters",
      "setLinkedAccess",
      "setAttendanceAccess",
      "setAudienceAccess",
    ]),
    setupEventListeners() {
      this.$root.$on("alert", this.handleAlert);
      this.$root.$on("drawer", this.handleDrawer);
      this.$root.$on("load-session", this.handleSession);
      this.setupSocketListeners();
    },
    setupSocketListeners() {
      eventBus.$on("attendance:queue", this.onQueue);
      eventBus.$on("attendance:started", this.onStarted);
      eventBus.$on("appointments:created", (data) => {
        console.log("evento criado pelo socket", data);
      });
      eventBus.$on("person:status", this.handleChangeStatus);
      eventBus.$on("attendance:finished", this.handleAttendanceFinished);

      eventBus.$on(
        "appointments:client:entered",
        this.getAppointmentsWaitingMe
      );
    },
    cleanupEventListeners() {
      this.$root.$off("alert", this.handleAlert);
      this.$root.$off("drawer", this.handleDrawer);
      this.$root.$off("load-session", this.handleSession);
      this.cleanupSocketListeners();
    },
    cleanupSocketListeners() {
      eventBus.$off("attendance:queue", this.onQueue);
      eventBus.$off("attendance:started", this.onStarted);
      eventBus.$off("person:status", this.handleChangeStatus);
      eventBus.$off("attendance:finished", this.handleAttendanceFinished);
      eventBus.$off(
        "appointments:client:entered",
        this.getAppointmentsWaitingMe
      );
    },
    async toAttendance() {
      const attendanceID =
        this.attendance?._id ||
        this.attendance?.id ||
        this.attendance?.attendanceID;

      this.loadingOverlay = false;

      if (!attendanceID) {
        this.setAttendance(null);

        return;
      }

      const { data } = await getAttendance(attendanceID);

      if (data?.status === "in-progress") {
        this.setAttendance(data);

        // A tela de fila deste admin é o Omnichannel — é para onde o menu
        // "Balcões" aponta. Mandar a notificação para a tela legada tirava o
        // atendente do contexto em que ele trabalha e escondia as outras
        // conversas abertas. O Omnichannel seleciona o atendimento pela query
        // `attendanceID`.
        this.$router
          .push({
            path: "/omnichannel",
            query: {
              attendanceID: this.attendance._id,
              typeStatus: "in-progress",
            },
          })
          .catch(() => {
            /* ignore */
          });
      } else {
        this.setAttendance(null);
      }
    },
    startNotificationSound() {
      if (this.notificationSoundInterval) {
        clearInterval(this.notificationSoundInterval);
      }

      this.notificationSoundInterval = setInterval(() => {
        this.playNotificationSound();
      }, 1500);
    },
    playNotificationSound() {
      Howler.autoUnlock = true;

      // Se já existir um som tocando, pare-o antes de tocar um novo
      if (this.notificationSound) {
        this.notificationSound.stop();
      }

      // Cria um novo som
      this.notificationSound = new Howl({
        src: [
          "https://storage.googleapis.com/notification-song/ringtone-1-46486.mp3",
        ],
        volume: 0.5, // Volume deve estar entre 0.0 e 1.0
      });

      // melodia de notificação
      this.notificationSound.play();
      setTimeout(() => {
        this.notificationSound.play();
      }, 300);
    },
    stopNotificationSound() {
      if (this.notificationSoundInterval) {
        clearInterval(this.notificationSoundInterval);
      }
    },

    async handleIsLinked() {
      if (!this.user) {
        return;
      }

      try {
        const attendances = await isLinkedPeople(this.user?._id, {
          module: "attendances",
        });

        const audiences = await isLinkedPeople(this.user?._id, {
          module: "audiences",
        });

        this.setAttendanceAccess(attendances?.data?.haveAssociations);
        this.setAudienceAccess(audiences?.data?.haveAssociations);

        if (
          !attendances.data?.haveAssociations &&
          !audiences.data?.haveAssociations
        ) {
          await this.setLinkedAccess(false);

          this.$router.push(
            "/user-not-linked",
            () => {},
            () => {}
          );

          return;
        }

        await this.setLinkedAccess(true);

        if (this.linkedAccess && this.$route.name === "UserNotLinked") {
          if (this.attendanceAccess)
            return this.$router.push(
              { name: "Home" },
              () => {},
              () => {}
            );
          if (this.audienceAccess)
            this.$router.push(
              { name: "Audiences" },
              () => {},
              () => {}
            );
        }
      } catch (error) {
        console.error("Error checking linked status:", error);
      }
    },

    async handleSession(signInOrigin = false) {
      const token = this.$store.getters.accessToken;

      if (token) {
        try {
          this.loading = true;
          const { data } = await loadSession();

          const currentUser = structuredClone(this.user);
          const samePermissionGroups = await this.compareArrays(
            currentUser.permissionGroups,
            data.permissionGroups
          );

          if (!samePermissionGroups) this.clearFilters();

          this.setUser(data);
          await this.handleIsLinked();

          if (data.status === "online" && this.linkedAccess) {
            if (
              !(this._isAdmin || this._isModerator) ||
              this.user?.settings?.attendancesNotification
            ) {
              this.getAttendancesWaitingMe();
              this.getAppointmentsWaitingMe();
            }
          }

          if (signInOrigin && this.linkedAccess) {
            if (this.attendanceAccess)
              return this.$router.push(
                { name: "Home" },
                () => {},
                () => {}
              );
            if (this.audienceAccess)
              this.$router.push(
                { name: "Audiences" },
                () => {},
                () => {}
              );
          }
        } catch {
          this.signOut(false);
        } finally {
          this.loading = false;
        }
      }

      this.loading = false;
      this.loaded = true;
    },

    async compareArrays(arr1, arr2) {
      const normalize = (array) =>
        array.map((obj) => JSON.stringify(obj)).sort();

      const sorted1 = normalize(arr1);
      const sorted2 = normalize(arr2);

      if (sorted1.length !== sorted2.length) return false;

      return sorted1.every((val, i) => val === sorted2[i]);
    },

    /**
     * Confirma no servidor se o atendente já está conduzindo algum atendimento.
     *
     * O estado local (`this.attendance`) não serve para isso: ele é zerado em
     * vários pontos do fluxo de chat e o login devolve o atendente para
     * "online", então sozinho ele deixa passar o alerta sonoro com o atendente
     * no meio de uma conversa.
     *
     * Em caso de erro responde `true` (ou seja, não notifica): perder uma
     * notificação é menos grave que tocar alarme durante um atendimento.
     */
    async hasAttendanceInProgress() {
      try {
        const { headers, data } = await getAttendancesInProgressMe();
        const count = Number(headers?.["x-count"] ?? (data || []).length);

        return count > 0;
      } catch (error) {
        console.log("hasAttendanceInProgress", error);

        return true;
      }
    },

    async getAttendancesWaitingMe() {
      try {
        // Verifica se o usuário está disponível para receber notificações
        if (!this._isAvailable) {
          return;
        }

        if (await this.hasAttendanceInProgress()) {
          this.newAttendanceDialog = false;

          return;
        }

        const { data } = await getAttendancesWaitingMe();

        if (!data) return;

        this.newAttendanceInfo = {
          groupName: data.comarca?.name || "",
          subgroupName: data.vara?.name || "",
          deskName: data.desk?.name || "",
        };

        this.newAttendanceDialog = true;
        this.newAttendanceID = data._id;
      } catch (error) {
        console.log(error);
      }
    },

    async signOut(handleRequest = true) {
      try {
        if (handleRequest) await signOut();
        await this.setSignOut();
        this.$router.push({ path: "/auth" }).catch(() => {
          /* ignore */
        });
      } catch (error) {
        this.handleAlert(error.data?.message || error.message, "error");
      }
    },

    /**
     * Gerencia a mudança de status do usuário
     * @param {Object|string} event - Objeto de pausa ou string de status
     */
    async changeStatus(event) {
      if (event?._id) {
        await this.handleAwayStatus(event);
        eventBus.$emit("new-pause");
      } else {
        await this.handleRegularStatus(event);
      }
    },

    /**
     * Gerencia a mudança para status "away" com uma pausa específica
     * @param {Object} pauseData - Dados da pausa selecionada
     */
    async handleAwayStatus(pauseData) {
      const payload = {
        _id: pauseData._id,
        name: pauseData.name,
        active: pauseData.active,
        duration: pauseData.duration,
        productive: pauseData.productive,
      };

      try {
        if (!this.user._id || !payload._id) {
          return this.handleAlert("Erro ao iniciar pausa", "error");
        }

        // Verifica se já existe uma pausa ativa e cancela
        if (
          this.$store.state.currentPauseBody &&
          this.$store.state.currentPauseBody.pauseID
        ) {
          // Cancela a pausa atual antes de iniciar uma nova

          await this.$refs.navDrawer.handleCancelPause();
        }

        // Atualiza o status no store para feedback imediato ao usuário
        this.updateUserStatus("away");

        // Atualiza o status no servidor
        await updateUser(this.user._id, { status: "away" });

        // Inicia a pausa no servidor
        const { data } = await startBreak(payload);

        if (data) {
          // Inicia o timer de pausa local
          if (this.startPause) {
            this.startPause(payload);
          }

          // Armazena os dados da pausa atual no store
          this.$store.dispatch("setCurrentPauseBody", {
            ...payload,
            pauseID: data._id,
          });
        }
      } catch (error) {
        this.handleStatusError(error);
      }
    },

    /**
     * Gerencia a mudança para status regular (online, busy, etc)
     * @param {string} newStatus - Novo status do usuário
     */
    async handleRegularStatus(newStatus) {
      try {
        await this.$refs.navDrawer.handleCancelPause();

        // Se o status for online, verifica se há pessoas na fila
        if (newStatus === "online") {
          this.getAttendancesWaitingMe();
        }
      } catch (error) {
        this.handleStatusError(error);
      }
    },

    /**
     * Atualiza o status do usuário no store
     * @param {string} newStatus - Novo status a ser definido
     */
    updateUserStatus(newStatus) {
      const updatedUser = { ...this.user, status: newStatus };
      this.setUser(updatedUser);
    },

    /**
     * Trata erros na mudança de status
     * @param {Error} error - Erro ocorrido
     */
    handleStatusError(error) {
      // Exibe mensagem de erro
      this.handleAlert(error.data?.message || error.message, "error");
    },

    /**
     * Atualiza o status do usuário para online quando uma pausa é limpa
     */
    async handlePauseCleared() {
      // Verifica se o status atual é "away"
      if (this.user.status === "away") {
        try {
          // Atualiza o status no store para feedback imediato
          this.updateUserStatus("online");

          // Atualiza o status no servidor
          await updateUser(this.user._id, { status: "online" });

          // Verifica se há pessoas na fila para o usuário
          this.getAttendancesWaitingMe();
        } catch (error) {
          // Em caso de erro, reverte para o status anterior
          this.handleStatusError(error, "away");
        }
      } else if (this.user.status === "online") {
        // Se já estiver online, verifica se há pessoas na fila
        this.getAttendancesWaitingMe();
      }
    },

    /**
     * Trata o evento de finalização de atendimento
     */
    handleAttendanceFinished() {
      if (event._id === this.attendance?._id && this.$route.name !== "Chat") {
        this.handleAlert(
          `Atendimento de Protocolo ${event.protocol} encerrado pelo solicitante.`,
          "info"
        );
        this.setAttendance(null);
      }

      // Quando um atendimento é finalizado, verifica se há pessoas na fila
      if (this.user.status === "online" && this.linkedAccess) {
        if (
          !(this._isAdmin || this._isModerator) ||
          this.user?.settings?.attendancesNotification
        ) {
          this.getAttendancesWaitingMe();
        }
      }
    },

    handleDrawer() {
      this.$refs.navDrawer.handleDrawer();
    },

    async onQueue(data) {
      try {
        const { _id: attendanceID, groupID, subgroupID, deskID, action } = data;

        if (!this._isAvailable) {
          this.newAttendanceDialog = false;

          return;
        }

        const isRequested = ["join", "transfer"].includes(action);

        if (!isRequested) {
          // Caso exista um atendimento notificando em aberto e ele saiu da fila, entao o dialog deve ser fechado
          if (this.newAttendanceID === attendanceID) {
            this.newAttendanceDialog = false;
            this.getAttendancesWaitingMe();
          }

          return;
        }

        let payload = null;

        if (this._isAdmin) {
          const isLinked = await this.isLinkedGroup(groupID);

          if (isLinked) {
            const groups = await getJurisdictions({
              personID: this.user._id,
              limit: 100,
            });

            payload = {
              groupIDs: groups.data.map((g) => g._id),
            };
          }
        }

        if (this._isModerator) {
          const isLinked = await this.isLinkedSubgroup(subgroupID);

          if (isLinked) {
            const subgroups = await getCourts({
              personID: this.user._id,
              limit: 100,
            });

            payload = {
              subgroupIDs: subgroups.data.map((g) => g._id),
            };
          }
        }

        if (this._isOperator || this._isSupervisor) {
          const isLinked = await this.isLinkedDesk(deskID);

          if (isLinked) {
            const desks = await getCounters({
              personID: this.user._id,
              limit: 100,
            });

            payload = {
              deskIDs: desks.data.map((g) => g._id),
            };
          }
        }

        if (payload) {
          const fifoID = await this.handleGetAttendanceFIFO(payload);

          let groupName = "";
          let subgroupName = "";
          let deskName = "";

          try {
            if (groupID) {
              const { data: groupData } = await getJurisdiction(groupID);
              groupName = groupData?.name || "";
            }

            if (subgroupID) {
              const { data: subgroupData } = await getCourt(subgroupID);
              subgroupName = subgroupData?.name || "";
            }

            if (deskID) {
              const { data: deskData } = await getCounter(deskID);
              deskName = deskData?.name || "";
            }
          } catch (error) {
            console.error("Erro ao buscar nomes:", error);
          }

          // Só alerta quem não está atendendo ninguém. Com multiatendimento o
          // atendente puxa a próxima conversa pela fila, sem ser interrompido.
          if (await this.hasAttendanceInProgress()) {
            this.newAttendanceDialog = false;

            return;
          }

          this.newAttendanceInfo = {
            groupName: groupName,
            subgroupName: subgroupName,
            deskName: deskName,
          };

          this.newAttendanceDialog = true;
          this.newAttendanceID = fifoID;
        }
      } catch (error) {
        this.processError(error, "onQueue");
        this.handleAlert(
          "Ocorreu um erro ao receber os dados da fila",
          "error"
        );
      }
    },

    onStarted() {},
    async handleStartAttendance() {
      this.loadingOverlay = true;

      try {
        const attendanceID = this.newAttendanceID;
        if (!attendanceID) {
          throw new Error("Ocorreu um erro ao iniciar o atendimento.");
        }

        const payload = {
          attendanceID,
        };

        await startAttendance(payload, (error, data) => {
          if (error) {
            this.loadingOverlay = false;
            const message =
              this.processError(error, "handleStartAttendance") ||
              "Ocorreu um erro ao iniciar atendimento.";
            this.handleAlert(message, "warning");
            return;
          }

          if (this.$route.name === "OmniChannel") {
            this.$router.replace({
              query: {
                typeStatus: "in-progress",
                attendanceID: data._id,
              },
            });
          } else {
            this.$router.push({
              name: "OmniChannel",
              query: {
                typeStatus: "in-progress",
                attendanceID: data._id,
              },
            });
          }

          this.loadingOverlay = false;
        });
      } catch (error) {
        this.processError(error, "handleStartAttendance");
        this.loadingOverlay = false;
      } finally {
        this.newAttendanceDialog = false;
        this.newAttendanceID = null;
      }
    },
    async handleGetAttendanceFIFO(payload) {
      try {
        const defaultQuery = {
          sortBy: "queuedAt",
          sortOrder: "asc",
          limit: 1,
          status: ["waiting"],
        };

        const { data } = await getAttendances({
          ...defaultQuery,
          ...payload,
        });
        return data[0]?._id;
      } catch (error) {
        this.processError(error, "handleGetAttendanceFIFO");
      }
    },
    async handleJoinAttendance() {
      this.loadingOverlay = true;

      try {
        this.handleAlert("Reconectando...", "info");

        const payload = {
          protocol: this.attendance.protocol,
        };

        await attendanceJoin(payload, (error, data) => {
          if (error) {
            this.loadingOverlay = false;
            this.handleAlert(
              "Falha ao Reconectar ao atendimento. Por favor, tente novamente."
            );
            return this.processError(error, "handleJoinAttendance");
          }

          this.handleAlert("Reconectado!", "success");

          this.setAttendance(data);
          this.loadingOverlay = false;
        });
      } catch (error) {
        this.processError(error, "handleJoinAttendance");
        this.loadingOverlay = false;
      } finally {
        this.loadingOverlay = false;
      }
    },
    async isLinkedDesk(deskID) {
      try {
        if (!deskID) {
          throw new Error("deskID is required");
        }

        const { data } = await getCounter(deskID);
        if (!data) {
          throw new Error("desk not found");
        }

        const isLinked =
          data.attendants?.some((e) => e._id === this.user._id) ||
          data.people?.some((e) => e._id === this.user._id);

        if (!isLinked) return false;

        return true;
      } catch (error) {
        this.processError(error, "isLinkedDesk");
        return false;
      }
    },
    async isLinkedSubgroup(subgroupID) {
      try {
        if (!subgroupID) {
          throw new Error("subgroupID is required");
        }

        const { data } = await getCourt(subgroupID);
        if (!data) {
          throw new Error("subgroup not found");
        }

        const isLinked = data.people?.some((e) => e._id === this.user._id);
        if (!isLinked) return false;

        return true;
      } catch (error) {
        this.processError(error, "isLinkedSubgroup");
        return false;
      }
    },
    async isLinkedGroup(groupID) {
      try {
        if (!groupID) {
          throw new Error("groupID is required");
        }

        const { data } = await getJurisdiction(groupID);
        if (!data) {
          throw new Error("group not found");
        }

        const isLinked = data.people?.some((e) => e._id === this.user._id);
        if (!isLinked) return false;

        return true;
      } catch (error) {
        this.processError(error, "isLinkedGroup");
        return false;
      }
    },

    async getAppointmentsWaitingMe() {
      if (
        this.user?.status !== "online" ||
        !this.user?.settings?.attendancesNotification
      )
        return;

      try {
        const { data } = await getAppointmentsWaitingMe();

        if (!data) return;

        this.newAppointmentInfo = {
          groupName: data.comarca?.name || "",
          subgroupName: data.vara?.name || "",
          deskName: data.desk?.name || "",
        };

        this.newAppointmentDialog = true;
        this.newAppointment = data;
      } catch (error) {
        console.log(error);
      }
    },

    async handleStartMeeting(appointment) {
      this.loadingOverlay = true;

      let newTab = null;

      try {
        const { data } = await getAppointmentById(appointment._id);

        if (data.status === "scheduled" && data.isReady) {
          newTab = window.open("about:blank", "_blank");

          const response = await startAppointment(appointment._id, {
            personID: this.user._id,
          });

          if (response.data.meetingLink) {
            newTab.location.href = response.data.meetingLink;
          }

          this.$router.push({
            name: "SchedulesQueueWaiting",
            query: { protocol: appointment.protocol },
          });

          return;
        }

        this.handleAlert("Este atendimento não está mais disponível", "error");
      } catch (error) {
        if (newTab) newTab.close();
        this.handleAlert("Erro ao iniciar agendamento", "error");
      } finally {
        this.newAppointmentDialog = false;
        this.newAppointmentInfo = {
          groupName: "",
          subgroupName: "",
          deskName: "",
        };
        this.newAppointment = null;

        this.loadingOverlay = false;
      }
    },

    processError(error, name = "") {
      const message =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message;
      console.log(`${name}:error = { ${message} }`);
      return message;
    },
    handleAlert(message, type, fixed, timeout = 3000) {
      this.$refs.alertBar?.handleAlert(message, type, fixed, timeout);
    },
    handlePingSession() {
      if (this.interval) clearInterval(this.interval);

      this.interval = setInterval(() => {
        // console.log("ping");
        pingEmit();
      }, 10000);
    },

    handleChangeToken(newVal, oldVal) {
      if (newVal && oldVal) {
        disconnectSocket();
        clearInterval(this.interval);

        setTimeout(() => {
          connectSocket();
          this.handlePingSession();

          this.$root.$emit("socket-reconnected");
        }, 1000);
      }
    },

    handleChangeStatus(data) {
      this.setUserStatus(data);

      if (
        (data.status === "online" || data.status === "busy") &&
        this.currentPauseBody?._id &&
        this.$refs.navDrawer
      ) {
        // Método que obtem a pausa atual, verifica se ta ativa e força o encerramento caso não esteja
        this.$refs.navDrawer.getPeopleBreakByID(true);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#app {
  background: #f3f3f3 !important;
}

body,
html {
  background: #f3f3f3 !important;
}

.float-drawer {
  z-index: 999999;
  top: 0.75rem !important;
  left: 0.25rem !important;
}

.button-pulse {
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
